import styled, { css } from 'styled-components'
import { black, blackOrange, darkGrey, gold, grey, orange, rail, textMuted, warmTint, white } from '@/theme/colors'
import { innerCardSurface, tintOnHover } from '@components/Card'
import { Button } from '@components/Button'
import { Pill } from '@components/Pill'
import { sortableItem, SortableItemProps } from '@components/SortableRow'
import { layers, media, motion, radii, shadows } from '@/theme/tokens'
import { leading, text } from '@/theme/typography'

export const PaneRoot = styled.section`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	padding: 18px 24px 0;

	${media.phone} {
		padding: 16px 16px 0;
	}
`

export const PaneHeader = styled.header`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 12px 16px;
	flex-wrap: wrap;
`

export const DayTitle = styled.h2`
	${text.heading}
	color: ${blackOrange};
	margin: 0;
	overflow-wrap: anywhere;
`

export const DaySubtitle = styled.div`
	${text.caption}
	color: ${textMuted};
	margin-top: 2px;
`

export const DayNote = styled.p`
	${text.bodyXs}
	color: ${textMuted};
	margin: 8px 0 0;
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
`

export const HeaderActions = styled.div`
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
	margin-left: auto;
`

export const DayActionButton = styled(Button).attrs({ variant: 'secondary' as const, size: 'sm' as const })``

/** The rows between the header and the bottom bar scroll on their own; the card's height is fixed. */
export const TimelineList = styled.div`
	flex: 1;
	min-height: 0;
	margin-top: 10px;
	padding-bottom: 8px;

	/* Only the fixed-height card scrolls its list; stacked, the page scrolls. */
	${media.aboveTabletWide} {
		overflow-y: auto;
		scrollbar-width: thin;
	}
`

const RAIL_WIDTH = 24

const railGrid = css`
	display: grid;
	grid-template-columns: ${RAIL_WIDTH}px minmax(0, 1fr);
	gap: 16px;
`

export const EventRowRoot = styled.div<SortableItemProps>`
	${railGrid}
	${sortableItem}
`

/**
 * The rail column: a solid 1px line through its centre, clipped to start at
 * the first dot and end at the last, with the dot painted on top.
 */
export const RailColumn = styled.div<{ $first?: boolean; $last?: boolean }>`
	position: relative;
	/* Full row height even where the row centres its content, so the line runs unbroken into a travel node. */
	align-self: stretch;
	display: flex;
	align-items: center;
	justify-content: center;

	&::before {
		content: '';
		position: absolute;
		left: ${RAIL_WIDTH / 2 - 0.5}px;
		width: 1px;
		top: ${({ $first }) => ($first ? '50%' : '0')};
		bottom: ${({ $last }) => ($last ? '50%' : '0')};
		background: ${rail};
	}
`

/** The event's number on the rail: the same orange disc with a white rim as its pin on the map. */
export const RailDot = styled.span`
	position: relative;
	z-index: ${layers.raised};
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 22px;
	height: 22px;
	border-radius: ${radii.round};
	background: ${orange};
	border: 2px solid ${white};
	box-sizing: border-box;
	color: ${white};
	${text.badge}
	line-height: ${leading.none};
`

/** `$sortable` false: the read-only card only opens on click, so it neither grabs nor claims touch gestures. */
export const EventCard = styled.div<{ $highlighted: boolean; $sortable: boolean }>`
	${innerCardSurface}
	${tintOnHover}
	margin: 8px 0;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 12px;
	cursor: ${({ $sortable }) => ($sortable ? 'grab' : 'pointer')};
	touch-action: ${({ $sortable }) => ($sortable ? 'none' : 'auto')};
	transition:
		background-color ${motion.fast},
		box-shadow ${motion.base};
	${({ $highlighted }) =>
		$highlighted &&
		css`
			background: ${warmTint};
			box-shadow: ${shadows.ring};
		`}

	&:active {
		cursor: ${({ $sortable }) => ($sortable ? 'grabbing' : 'pointer')};
	}
`

export const EventBody = styled.div`
	min-width: 0;
	flex: 1;
`

export const EventName = styled.div`
	${text.label}
	color: ${black};
	overflow-wrap: anywhere;
`

export const EventMeta = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 4px;
	${text.caption}
	color: ${textMuted};
	min-width: 0;
`

export const TimePill = styled(Pill).attrs({ $tone: 'muted' as const })``

export const PinGlyph = styled.span<{ $mapped: boolean }>`
	display: inline-flex;
	flex: none;
	color: ${({ $mapped }) => ($mapped ? textMuted : darkGrey)};
`

export const EventAddress = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

/* Travel gap rows */

export const GapRowRoot = styled.div<{ $state: 'hidden' | 'calculating' | 'route' }>`
	${railGrid}
	align-items: center;
	height: ${({ $state }) => ($state === 'hidden' ? '8px' : '40px')};
`

export const TravelNode = styled.span`
	position: relative;
	z-index: ${layers.raised};
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	border-radius: ${radii.round};
	background: ${gold};
	color: ${white};
`

export const TravelText = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	${text.caption}
	color: ${black};
	min-width: 0;
`

export const TravelDot = styled.span`
	width: 3px;
	height: 3px;
	border-radius: ${radii.round};
	background: ${black};
	flex: none;
`

export const DirectionsLink = styled(Button).attrs({ variant: 'ghost' as const, size: 'sm' as const })`
	padding: 2px 6px;
	color: ${orange};
	margin-left: 4px;
`

/* Bottom bar and empty state */

export const BottomBar = styled.div`
	flex: none;
	border-top: 1px solid ${grey};
	padding: 12px 24px 16px;

	${media.phone} {
		padding: 12px 16px 16px;
	}
`

export const BottomActions = styled.div`
	display: flex;
	gap: 8px;
`

export const AddEventBar = styled(Button)`
	flex: 1;
	min-width: 0;
`

export const EmptyDay = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
`
